import React, { useEffect, useState } from "react";
import { X, Loader2, Receipt, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * RefundCreditModal , issue a refund or store-credit note against an order.
 * Records the note in `refund_notes` and (optionally) tops up the customer's
 * Heritage credit balance. Lives behind a button on ConsoleOrderDrawer.
 */
export default function RefundCreditModal({ order, onClose, onDone }) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    kind: "credit_note",
    amount: "",
    reason: "",
    apply_to_credits: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/console/orders/${order.id}/refunds`);
        setRefunds(r.data?.refunds || []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [order.id]);

  const total = Number(order.total || 0);
  const alreadyIssued = refunds.reduce((s, r) => s + Number(r.amount || 0), 0);
  const remaining = Math.max(0, total - alreadyIssued);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount."); return; }
    if (amt > remaining + 0.01) { toast.error(`Maximum remaining is $${remaining.toFixed(2)}.`); return; }
    if (!form.reason.trim()) { toast.error("Please add a reason for the audit log."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/console/orders/${order.id}/refund`, {
        kind: form.kind,
        amount: amt,
        reason: form.reason.trim(),
        apply_to_credits: form.apply_to_credits,
      });
      const verb = form.kind === "refund" ? "Refund" : "Credit note";
      toast.success(`${verb} of $${amt.toFixed(2)} issued.${r.data?.applied_to_credits ? " Customer credits topped up." : ""}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't issue refund.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="refund-modal">
      <div className="bg-[#FBF6E9] border border-[#E8DFCB] rounded-md w-full max-w-lg max-h-[90vh] overflow-auto">
        <header className="flex items-center justify-between p-5 border-b border-[#E8DFCB] sticky top-0 bg-[#FBF6E9]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Refund / Credit Note
            </div>
            <h2 className="font-serif text-xl text-[#3C2A1E] mt-0.5">Order {order.order_number || order.id?.slice(0, 8)}</h2>
          </div>
          <button onClick={onClose} data-testid="refund-modal-close" className="p-2 hover:bg-[#E8DFCB] rounded">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Stat label="Order total" value={`$${total.toFixed(2)}`} />
            <Stat label="Already issued" value={`$${alreadyIssued.toFixed(2)}`} />
            <Stat label="Remaining" value={`$${remaining.toFixed(2)}`} tone={remaining > 0 ? "ok" : "muted"} />
          </div>

          {/* Existing refunds */}
          {loading ? null : refunds.length > 0 && (
            <div className="bg-white border border-[#E8DFCB] rounded p-3 text-xs">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-2">Previous</div>
              <ul className="space-y-1">
                {refunds.map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-[#3C2A1E]">
                    <span>
                      <strong className="capitalize">{(r.kind || "").replace("_", " ")}</strong> · ${Number(r.amount).toFixed(2)}
                      <span className="text-[#9C8B73] ml-2">{(r.issued_at || "").slice(0, 10)}</span>
                    </span>
                    <span className="text-[#7A6A55] italic max-w-[60%] text-right truncate" title={r.reason}>{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {remaining <= 0 ? (
            <div className="bg-[#FFF4F0] border border-[#E8B5A2] rounded p-3 text-sm text-[#A33030] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Order is fully refunded / credited.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-1.5">
                  Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "credit_note", label: "Store credit", help: "Issue Heritage credits (online refund equivalent)." },
                    { v: "refund",      label: "Refund record",      help: "Log a real refund (Stripe/bank done outside the app)." },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`refund-kind-${opt.v}`}
                      onClick={() => setForm((f) => ({ ...f, kind: opt.v }))}
                      className={`text-left px-3 py-2.5 rounded border text-xs transition ${form.kind === opt.v
                        ? "bg-[#6E1E3A] text-white border-[#6E1E3A]"
                        : "bg-white text-[#3C2A1E] border-[#E8DFCB] hover:border-[#6E1E3A]"}`}
                    >
                      <div className="font-serif text-sm">{opt.label}</div>
                      <div className={`text-[10px] mt-0.5 ${form.kind === opt.v ? "opacity-85" : "text-[#7A6A55]"}`}>{opt.help}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-1">Amount (NZD)</span>
                  <input
                    data-testid="refund-amount"
                    type="number" step="0.01" min="0.01" max={remaining}
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder={`Max $${remaining.toFixed(2)}`}
                    className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded font-serif text-base focus:outline-none focus:border-[#6E1E3A]"
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    data-testid="refund-full"
                    onClick={() => setForm((f) => ({ ...f, amount: remaining.toFixed(2) }))}
                    className="px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A] border border-[#6E1E3A] rounded hover:bg-[#6E1E3A]/5"
                  >
                    Full remaining
                  </button>
                </div>
              </div>

              <label className="block">
                <span className="block text-[10px] tracking-[0.3em] uppercase text-[#6E1E3A] font-semibold mb-1">Reason / audit note</span>
                <textarea
                  data-testid="refund-reason"
                  rows={2}
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Two tubers arrived soft, agreed to credit half order."
                  className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm focus:outline-none focus:border-[#6E1E3A]"
                />
              </label>

              <label className="flex items-start gap-2 text-sm text-[#3C2A1E]">
                <input
                  data-testid="refund-apply-credits"
                  type="checkbox"
                  checked={form.apply_to_credits}
                  onChange={(e) => setForm((f) => ({ ...f, apply_to_credits: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <strong>Top up customer Heritage credits</strong>{" "}
                  <span className="text-[#7A6A55]">, recommended for store credit; uncheck for refund-only records.</span>
                </span>
              </label>

              <div className="flex gap-2 pt-2 border-t border-[#E8DFCB]">
                <button type="button" onClick={onClose} className="flex-1 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#3C2A1E] text-[#3C2A1E] rounded hover:bg-[#FBF6E9]">
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="refund-submit"
                  disabled={busy}
                  className="flex-1 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                >
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {busy ? "Issuing…" : `Issue ${form.kind === "refund" ? "refund" : "credit note"}`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "muted" }) {
  const c = { ok: "bg-[#EBF3E5] text-[#2F4A2A]", muted: "bg-white text-[#3C2A1E] border border-[#E8DFCB]" }[tone];
  return (
    <div className={`${c} rounded p-2 text-center`}>
      <div className="text-[9px] tracking-[0.25em] uppercase opacity-70">{label}</div>
      <div className="font-serif text-base mt-0.5">{value}</div>
    </div>
  );
}
